import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, PlayCircle, Search } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { HideWhenSingleCompany } from "@/components/CompanyField";


export default function DepreciationRuns() {
  const { can } = usePermissions();
  const canWrite = can("ASSETS_PLANT", "CREATE");
  const { companies } = useCompany() || {};
  const [companyId, setCompanyId] = useState("");
  const [period, setPeriod] = useState("");
  const [runs, setRuns] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (companies?.length && !companyId) setCompanyId(companies[0].id);
  }, [companies, companyId]);

  const loadRuns = async () => {
    if (!companyId) return;
    const { data } = await api.get(`/companies/${companyId}/depreciation-runs`);
    setRuns(data);
  };
  useEffect(() => { loadRuns(); }, [companyId]);

  const preview = async () => {
    if (!period) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/depreciation-runs`, { period });
      setCurrent(data);
      toast.success(data.status === "POSTED" ? "This run was already posted" : `Previewed ${data.lines.length} asset(s)`);
      loadRuns();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const post = async () => {
    setPosting(true);
    try {
      const { data } = await api.post(`/depreciation-runs/${current.id}/post`);
      setCurrent(data);
      toast.success("Depreciation posted to GL");
      loadRuns();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="depreciation-runs-page">
      <PageHeader title="Depreciation Runs" description="One run per company per period. Previewing is safe to repeat; posting is idempotent." />

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-4">
        <HideWhenSingleCompany><div>
          <Label>Company</Label>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="mt-1.5 w-56" data-testid="depreciation-company-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div></HideWhenSingleCompany>
        <div>
          <Label>Period (YYYY-MM)</Label>
          <Input placeholder="2026-08" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1.5 w-40" data-testid="depreciation-period-input" />
        </div>
        {canWrite && (
          <Button disabled={!companyId || !period || loading} onClick={preview} data-testid="preview-depreciation-run-button">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} <Search className="h-3.5 w-3.5" /> Preview
          </Button>
        )}
      </div>

      {current && (
        <div className="rounded-xl border border-border bg-card" data-testid="depreciation-run-preview">
          <div className="flex items-center justify-between border-b border-border p-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Period {current.period}</h2>
              <p className="text-xs text-muted-foreground">{current.lines.length} asset(s) depreciating this period</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill value={current.status} />
              {canWrite && current.status === "DRAFT" && current.lines.length > 0 && (
                <Button size="sm" disabled={posting} onClick={post} data-testid="post-depreciation-run-button">
                  {posting && <Loader2 className="h-4 w-4 animate-spin" />} <PlayCircle className="h-3.5 w-3.5" /> Post to GL
                </Button>
              )}
            </div>
          </div>
          {current.lines.length === 0 ? (
            <EmptyState title="No active depreciable assets for this period" testId="depreciation-run-empty-state" />
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead className="text-right">Opening book value</TableHead><TableHead className="text-right">Depreciation</TableHead><TableHead className="text-right">Closing book value</TableHead></TableRow></TableHeader>
              <TableBody>
                {current.lines.map((l) => (
                  <TableRow key={l.id} data-testid={`depreciation-line-row-${l.asset_id}`}>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">{fmtNum(l.opening_book_value, 2)}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtNum(l.depreciation_amount, 2)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(l.closing_book_value, 2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card">
        <div className="border-b border-border p-4"><h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Run history</h2></div>
        {runs.length === 0 ? (
          <EmptyState title="No depreciation runs yet" testId="depreciation-runs-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Assets</TableHead></TableRow></TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} data-testid={`depreciation-run-row-${r.period}`}>
                  <TableCell className="font-mono text-xs text-foreground">{r.period}</TableCell>
                  <TableCell><StatusPill value={r.status} /></TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{r.lines.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
